import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
// Note: useMemo is used both for migration at init and for exportData/quantities
import {
  generateId,
  distance,
  polygonArea as calcPolygonArea,
  pointToLineDistance,
  projectPointOnLine,
  getEdgeOverlapOnWall,
  generateArcPoints,
  calculateOuterPoints,
} from '../utils/geometry';
import { DEFAULT_STOREY_HEIGHT, SIZES, POINT_TYPES } from '../utils/constants';

const MAX_HISTORY_SIZE = 50;

// Helper: Migriere bestehende Wände ohne Außenpunkte (synchron beim Init)
// Erstellt Außenpunkte für Wände die noch keine haben
function migrateWallsWithOuterPoints(initialPoints, initialWalls, storeyHeight) {
  // Wenn keine Wände, nichts zu migrieren
  if (!initialWalls || initialWalls.length === 0) {
    return { points: initialPoints || [], walls: initialWalls || [] };
  }

  // Prüfen ob Migration nötig ist (mind. eine Wand ohne Außenpunkte)
  const wallsNeedingMigration = initialWalls.filter(w => !w.outerStartPointId || !w.outerEndPointId);
  if (wallsNeedingMigration.length === 0) {
    return { points: initialPoints, walls: initialWalls };
  }

  console.log(`Migration: ${wallsNeedingMigration.length} Wände benötigen Außenpunkte`);

  // Kopien erstellen um Originale nicht zu mutieren
  const newPoints = [...(initialPoints || [])];
  const newWalls = initialWalls.map(wall => {
    // Wand hat bereits Außenpunkte
    if (wall.outerStartPointId && wall.outerEndPointId) {
      return wall;
    }

    // Innenpunkte finden
    const innerStart = newPoints.find(p => p.id === wall.startPointId);
    const innerEnd = newPoints.find(p => p.id === wall.endPointId);

    if (!innerStart || !innerEnd) {
      console.warn('Migration: Innenpunkte nicht gefunden für Wand', wall.id);
      return wall;
    }

    // Außenpunkte berechnen
    const thickness = wall.thickness || 125;
    const flipped = wall.flipped || false;
    const { outerStart, outerEnd } = calculateOuterPoints(innerStart, innerEnd, thickness, flipped);

    // Neue Außenpunkte erstellen
    const outerStartPoint = {
      id: generateId(),
      x: outerStart.x,
      y: outerStart.y,
      z: innerStart.z ?? storeyHeight,
      type: POINT_TYPES.WALL_OUTER,
      wallId: wall.id,
      linkedToPointId: wall.startPointId,
    };

    const outerEndPoint = {
      id: generateId(),
      x: outerEnd.x,
      y: outerEnd.y,
      z: innerEnd.z ?? storeyHeight,
      type: POINT_TYPES.WALL_OUTER,
      wallId: wall.id,
      linkedToPointId: wall.endPointId,
    };

    // Punkte hinzufügen
    newPoints.push(outerStartPoint, outerEndPoint);

    // Wand aktualisieren
    return {
      ...wall,
      outerStartPointId: outerStartPoint.id,
      outerEndPointId: outerEndPoint.id,
      thickness,
      flipped,
    };
  });

  console.log(`Migration abgeschlossen: ${newPoints.length} Punkte, ${newWalls.length} Wände`);
  return { points: newPoints, walls: newWalls };
}

export function useEditorState(initialStorey) {
  // Geschosshöhe: Muss mindestens 1000mm (1m) sein, sonst Default verwenden
  // Verhindert Fehler wenn Daten in falscher Einheit (z.B. Meter statt mm) gespeichert wurden
  const rawStoreyHeight = initialStorey?.height;
  const storeyHeight = (rawStoreyHeight && rawStoreyHeight >= 1000) ? rawStoreyHeight : DEFAULT_STOREY_HEIGHT;

  // Migration synchron beim Init durchführen (nicht in useEffect!)
  const migrated = useMemo(() => {
    return migrateWallsWithOuterPoints(
      initialStorey?.points || [],
      initialStorey?.walls || [],
      storeyHeight
    );
  }, []); // Nur einmal beim Mount

  const [points, setPoints] = useState(migrated.points);
  const [walls, setWalls] = useState(migrated.walls);
  const [spaces, setSpaces] = useState(initialStorey?.spaces || []);
  const [shafts, setShafts] = useState(initialStorey?.shafts || []);
  const [stubWalls, setStubWalls] = useState(initialStorey?.stubWalls || []);
  const [arcs, setArcs] = useState(initialStorey?.arcs || []); // Arc-Metadaten
  const [solids, setSolids] = useState(initialStorey?.solids || []); // 3D-Körper (Boxes, etc.)

  // History für Undo/Redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isRestoringRef = useRef(false);
  const snapshotTimeoutRef = useRef(null);

  // Snapshot erstellen (debounced, um mehrere schnelle Änderungen zu gruppieren)
  const saveSnapshot = useCallback(() => {
    if (isRestoringRef.current) return;

    // Debounce: Nur einen Snapshot pro Frame
    if (snapshotTimeoutRef.current) {
      clearTimeout(snapshotTimeoutRef.current);
    }

    snapshotTimeoutRef.current = setTimeout(() => {
      const snapshot = {
        points: JSON.parse(JSON.stringify(points)),
        walls: JSON.parse(JSON.stringify(walls)),
        spaces: JSON.parse(JSON.stringify(spaces)),
        shafts: JSON.parse(JSON.stringify(shafts)),
        stubWalls: JSON.parse(JSON.stringify(stubWalls)),
        arcs: JSON.parse(JSON.stringify(arcs)),
        solids: JSON.parse(JSON.stringify(solids)),
        timestamp: Date.now(),
      };

      setHistory(prev => {
        // Alles nach aktuellem Index entfernen (neuer Branch)
        const trimmed = prev.slice(0, historyIndex + 1);
        const updated = [...trimmed, snapshot];

        // Maximale Größe einhalten
        if (updated.length > MAX_HISTORY_SIZE) {
          return updated.slice(updated.length - MAX_HISTORY_SIZE);
        }
        return updated;
      });

      setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY_SIZE - 1));
      snapshotTimeoutRef.current = null;
    }, 50); // 50ms debounce
  }, [points, walls, spaces, shafts, stubWalls, arcs, historyIndex]);

  // Initialen Snapshot erstellen
  useEffect(() => {
    if (history.length === 0 && (points.length > 0 || walls.length > 0 || spaces.length > 0)) {
      const initialSnapshot = {
        points: JSON.parse(JSON.stringify(points)),
        walls: JSON.parse(JSON.stringify(walls)),
        spaces: JSON.parse(JSON.stringify(spaces)),
        shafts: JSON.parse(JSON.stringify(shafts)),
        stubWalls: JSON.parse(JSON.stringify(stubWalls)),
        timestamp: Date.now(),
      };
      setHistory([initialSnapshot]);
      setHistoryIndex(0);
    }
  }, []); // Nur beim ersten Mount

  // Undo
  const undo = useCallback(() => {
    if (historyIndex <= 0) return false;

    const newIndex = historyIndex - 1;
    const snapshot = history[newIndex];

    if (snapshot) {
      isRestoringRef.current = true;
      setPoints(snapshot.points);
      setWalls(snapshot.walls);
      setSpaces(snapshot.spaces);
      setShafts(snapshot.shafts || []);
      setStubWalls(snapshot.stubWalls || []);
      setArcs(snapshot.arcs || []);
      setSolids(snapshot.solids || []);
      setHistoryIndex(newIndex);

      // Flag zurücksetzen nach dem Render
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 100);
      return true;
    }
    return false;
  }, [historyIndex, history]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return false;

    const newIndex = historyIndex + 1;
    const snapshot = history[newIndex];

    if (snapshot) {
      isRestoringRef.current = true;
      setPoints(snapshot.points);
      setWalls(snapshot.walls);
      setSpaces(snapshot.spaces);
      setShafts(snapshot.shafts || []);
      setStubWalls(snapshot.stubWalls || []);
      setArcs(snapshot.arcs || []);
      setSolids(snapshot.solids || []);
      setHistoryIndex(newIndex);

      // Flag zurücksetzen nach dem Render
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 100);
      return true;
    }
    return false;
  }, [historyIndex, history]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Punkt nach ID finden
  const getPoint = useCallback((pointId) => {
    return points.find(p => p.id === pointId);
  }, [points]);

  // Polygon-Koordinaten für einen Raum
  const getSpacePolygon = useCallback((space) => {
    return (space.pointIds || [])
      .map(pid => getPoint(pid))
      .filter(Boolean);
  }, [getPoint]);

  // Wände eines Raums finden
  const getWallsForSpace = useCallback((spaceId) => {
    return walls.filter(w => w.spaceIds?.includes(spaceId));
  }, [walls]);

  // Wand zwischen zwei Punkten finden
  const findWallBetweenPoints = useCallback((p1Id, p2Id) => {
    return walls.find(w =>
      (w.startPointId === p1Id && w.endPointId === p2Id) ||
      (w.startPointId === p2Id && w.endPointId === p1Id)
    );
  }, [walls]);

  // Nächsten Punkt finden (für Snap)
  const findNearestPoint = useCallback((x, y, threshold = SIZES.snapThreshold) => {
    let nearest = null;
    let minDist = threshold;

    for (const p of points) {
      const d = distance({ x, y }, p);
      if (d < minDist) {
        minDist = d;
        nearest = p;
      }
    }

    return nearest;
  }, [points]);

  // Wand an Position finden (für Wall-Splitting)
  const findWallAtPoint = useCallback((x, y, threshold = SIZES.snapThreshold) => {
    const point = { x, y };

    for (const wall of walls) {
      const start = points.find(p => p.id === wall.startPointId);
      const end = points.find(p => p.id === wall.endPointId);

      if (!start || !end) continue;

      const dist = pointToLineDistance(point, start, end);

      if (dist < threshold) {
        const projected = projectPointOnLine(point, start, end);
        // Nur wenn der Punkt wirklich auf der Wand liegt (nicht an den Enden)
        if (projected.t > 0.05 && projected.t < 0.95) {
          return {
            wall,
            projectedPoint: { x: projected.x, y: projected.y },
            t: projected.t,
            distance: dist,
          };
        }
      }
    }

    return null;
  }, [walls, points]);

  // Neuen Punkt erstellen
  // type: 'wall' (Standard) = Wandpunkt, 'auxiliary' = Hilfspunkt für Schächte/Vorwände
  // z kann undefined/null sein - dann wird storeyHeight verwendet (nullish coalescing)
  const addPoint = useCallback((x, y, z, type = POINT_TYPES.WALL) => {
    const newPoint = { id: generateId(), x, y, z: z ?? storeyHeight, type };
    setPoints(prev => [...prev, newPoint]);
    setTimeout(saveSnapshot, 0); // Snapshot nach State-Update
    return newPoint;
  }, [storeyHeight, saveSnapshot]);

  // Punkt verschieben (kein Snapshot bei jedem Drag - nur am Ende)
  // Bei Innenpunkten werden auch verknüpfte Außenpunkte aktualisiert
  const movePoint = useCallback((pointId, x, y) => {
    setPoints(prev => {
      // Punkt bewegen
      const updated = prev.map(p =>
        p.id === pointId ? { ...p, x, y } : p
      );

      // Prüfen ob dieser Punkt ein Innenpunkt einer Wand ist
      // Dann müssen die zugehörigen Außenpunkte neu berechnet werden
      const movedPoint = updated.find(p => p.id === pointId);
      if (!movedPoint || movedPoint.type === POINT_TYPES.WALL_OUTER) {
        return updated; // Außenpunkte nicht direkt bewegen lassen
      }

      // Finde alle Wände die diesen Innenpunkt verwenden
      const affectedWalls = walls.filter(w =>
        w.startPointId === pointId || w.endPointId === pointId
      );

      if (affectedWalls.length === 0) return updated;

      // Für jede betroffene Wand: Außenpunkte neu berechnen
      return updated.map(p => {
        // Nur Außenpunkte die zu betroffenen Wänden gehören
        if (p.type !== POINT_TYPES.WALL_OUTER) return p;

        const wall = affectedWalls.find(w =>
          w.outerStartPointId === p.id || w.outerEndPointId === p.id
        );
        if (!wall) return p;

        // Inner points für diese Wand finden
        const innerStart = updated.find(pt => pt.id === wall.startPointId);
        const innerEnd = updated.find(pt => pt.id === wall.endPointId);
        if (!innerStart || !innerEnd) return p;

        // Außenpunkte neu berechnen
        const { outerStart, outerEnd } = calculateOuterPoints(
          innerStart, innerEnd, wall.thickness || 125, wall.flipped || false
        );

        if (p.id === wall.outerStartPointId) {
          return { ...p, x: outerStart.x, y: outerStart.y };
        } else if (p.id === wall.outerEndPointId) {
          return { ...p, x: outerEnd.x, y: outerEnd.y };
        }
        return p;
      });
    });
    // Kein automatischer Snapshot beim Drag - wird manuell beim DragEnd aufgerufen
  }, [walls]);

  // Punkt verschieben mit Snapshot (für DragEnd)
  const movePointWithSnapshot = useCallback((pointId, x, y) => {
    // Gleiche Logik wie movePoint, aber mit Snapshot
    movePoint(pointId, x, y);
    setTimeout(saveSnapshot, 0);
  }, [movePoint, saveSnapshot]);

  // Punkt aktualisieren
  const updatePoint = useCallback((pointId, updates) => {
    setPoints(prev => prev.map(p =>
      p.id === pointId ? { ...p, ...updates } : p
    ));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // Punkt löschen (nur wenn nicht mehr verwendet)
  const deletePoint = useCallback((pointId) => {
    const usedByWalls = walls.some(w =>
      w.startPointId === pointId || w.endPointId === pointId
    );
    const usedBySpaces = spaces.some(s =>
      s.pointIds?.includes(pointId)
    );
    const usedByShafts = shafts.some(s =>
      s.pointIds?.includes(pointId)
    );
    const usedByStubWalls = stubWalls.some(sw =>
      sw.pointIds?.includes(pointId)
    );

    if (!usedByWalls && !usedBySpaces && !usedByShafts && !usedByStubWalls) {
      setPoints(prev => prev.filter(p => p.id !== pointId));
      setTimeout(saveSnapshot, 0);
      return true;
    }
    return false;
  }, [walls, spaces, shafts, stubWalls, saveSnapshot]);

  // Zwei Punkte verschmelzen (sourcePoint wird in targetPoint überführt)
  const mergePoints = useCallback((sourcePointId, targetPointId) => {
    if (sourcePointId === targetPointId) return false;

    // Alle Referenzen in Wänden aktualisieren
    setWalls(prev => {
      const updated = prev.map(w => ({
        ...w,
        startPointId: w.startPointId === sourcePointId ? targetPointId : w.startPointId,
        endPointId: w.endPointId === sourcePointId ? targetPointId : w.endPointId,
      }));
      // Wände entfernen, bei denen Start und Ende jetzt gleich sind
      return updated.filter(w => w.startPointId !== w.endPointId);
    });

    // Alle Referenzen in Räumen aktualisieren
    setSpaces(prev => prev.map(s => ({
      ...s,
      pointIds: (s.pointIds || []).map(pid =>
        pid === sourcePointId ? targetPointId : pid
      ).filter((pid, idx, arr) =>
        // Doppelte aufeinanderfolgende Punkte entfernen
        idx === 0 || pid !== arr[idx - 1]
      ),
    })));

    // Alle Referenzen in Schächten aktualisieren
    setShafts(prev => prev.map(s => ({
      ...s,
      pointIds: (s.pointIds || []).map(pid =>
        pid === sourcePointId ? targetPointId : pid
      ).filter((pid, idx, arr) =>
        idx === 0 || pid !== arr[idx - 1]
      ),
    })));

    // Alle Referenzen in Vorwänden aktualisieren
    setStubWalls(prev => prev.map(sw => ({
      ...sw,
      pointIds: (sw.pointIds || []).map(pid =>
        pid === sourcePointId ? targetPointId : pid
      ).filter((pid, idx, arr) =>
        idx === 0 || pid !== arr[idx - 1]
      ),
    })));

    // Source-Punkt löschen
    setPoints(prev => prev.filter(p => p.id !== sourcePointId));

    setTimeout(saveSnapshot, 0);
    return true;
  }, [saveSnapshot]);

  // Wand teilen an einem Punkt
  const splitWallAtPoint = useCallback((wallId, newPointId) => {
    const wall = walls.find(w => w.id === wallId);
    if (!wall) return null;

    // Punkte holen für Längenberechnung
    const startPoint = points.find(p => p.id === wall.startPointId);
    const endPoint = points.find(p => p.id === wall.endPointId);
    const newPoint = points.find(p => p.id === newPointId);

    // Berechne Splitposition (0-1 entlang der Originalwand)
    let splitT = 0.5; // Fallback
    if (startPoint && endPoint && newPoint) {
      const totalLength = Math.hypot(endPoint.x - startPoint.x, endPoint.y - startPoint.y);
      const firstLength = Math.hypot(newPoint.x - startPoint.x, newPoint.y - startPoint.y);
      if (totalLength > 0) {
        splitT = firstLength / totalLength;
      }
    }

    // Öffnungen auf neue Wände verteilen (L3 Fix)
    const openings1 = [];
    const openings2 = [];

    for (const opening of (wall.openings || [])) {
      const openingPos = opening.position ?? 0.5;
      if (openingPos < splitT) {
        // Öffnung gehört zu wall1 - Position neu berechnen
        openings1.push({
          ...opening,
          id: generateId(), // Neue ID um Duplikate zu vermeiden
          position: splitT > 0 ? openingPos / splitT : 0.5,
        });
      } else {
        // Öffnung gehört zu wall2 - Position neu berechnen
        openings2.push({
          ...opening,
          id: generateId(),
          position: (1 - splitT) > 0 ? (openingPos - splitT) / (1 - splitT) : 0.5,
        });
      }
    }

    // Zwei neue Wände erstellen
    const wall1 = {
      id: generateId(),
      startPointId: wall.startPointId,
      endPointId: newPointId,
      spaceIds: [...(wall.spaceIds || [])],
      typeRef: wall.typeRef,
      openings: openings1,
    };

    const wall2 = {
      id: generateId(),
      startPointId: newPointId,
      endPointId: wall.endPointId,
      spaceIds: [...(wall.spaceIds || [])],
      typeRef: wall.typeRef,
      openings: openings2,
    };

    // Alte Wand entfernen, neue hinzufügen
    setWalls(prev => [
      ...prev.filter(w => w.id !== wallId),
      wall1,
      wall2,
    ]);

    // Spaces aktualisieren: Punkt in pointIds einfügen
    setSpaces(prev => prev.map(space => {
      if (!wall.spaceIds?.includes(space.id)) return space;

      const pointIds = [...(space.pointIds || [])];
      const startIdx = pointIds.indexOf(wall.startPointId);
      const endIdx = pointIds.indexOf(wall.endPointId);

      if (startIdx === -1 || endIdx === -1) {
        console.warn('splitWallAtPoint: Wall endpoints not found in space pointIds', {
          spaceId: space.id,
          wallStartId: wall.startPointId,
          wallEndId: wall.endPointId,
          pointIds
        });
        return space;
      }

      // Punkt zwischen Start und End einfügen
      let inserted = false;
      if ((startIdx + 1) % pointIds.length === endIdx) {
        // Start kommt direkt vor End
        pointIds.splice(startIdx + 1, 0, newPointId);
        inserted = true;
      } else if ((endIdx + 1) % pointIds.length === startIdx) {
        // End kommt direkt vor Start
        pointIds.splice(endIdx + 1, 0, newPointId);
        inserted = true;
      } else {
        // Fallback: Versuche den Punkt an der richtigen Stelle einzufügen
        // Die Punkte sollten benachbart sein, aber vielleicht in einer unerwarteten Reihenfolge
        console.warn('splitWallAtPoint: Points not adjacent, trying fallback', {
          startIdx, endIdx, length: pointIds.length
        });
        // Füge nach dem kleineren Index ein
        const insertIdx = Math.min(startIdx, endIdx) + 1;
        pointIds.splice(insertIdx, 0, newPointId);
        inserted = true;
      }

      console.log('splitWallAtPoint: Updated space pointIds', {
        spaceId: space.id,
        spaceName: space.name,
        oldLength: space.pointIds?.length,
        newLength: pointIds.length,
        newPointId,
        inserted
      });

      return { ...space, pointIds };
    }));

    setTimeout(saveSnapshot, 0);
    return { wall1, wall2 };
  }, [walls, points, saveSnapshot]);

  // Neue Wand erstellen oder Raum zu bestehender hinzufügen
  // spaceId: wenn angegeben, wird der Raum der Innenseite zugewiesen
  // isOuterSpace: wenn true, wird der Raum der Außenseite zugewiesen
  const addWall = useCallback((startPointId, endPointId, spaceId = null, isOuterSpace = false) => {
    const existing = findWallBetweenPoints(startPointId, endPointId);

    if (existing) {
      // Bestehende Wand: Raum zu passender Seite hinzufügen
      if (spaceId) {
        setWalls(prev => prev.map(w => {
          if (w.id !== existing.id) return w;
          if (isOuterSpace) {
            return { ...w, outerSpaceId: spaceId };
          } else {
            return { ...w, innerSpaceId: spaceId };
          }
        }));
        setTimeout(saveSnapshot, 0);
      }
      return existing;
    }

    // Innenpunkte holen
    const innerStart = points.find(p => p.id === startPointId);
    const innerEnd = points.find(p => p.id === endPointId);
    if (!innerStart || !innerEnd) {
      console.error('addWall: Inner points not found', { startPointId, endPointId });
      return null;
    }

    const wallId = generateId();
    const thickness = 125; // Standard-Wandstärke
    const flipped = false;

    // Außenpunkte berechnen und erstellen
    const { outerStart, outerEnd } = calculateOuterPoints(innerStart, innerEnd, thickness, flipped);

    const outerStartPoint = {
      id: generateId(),
      x: outerStart.x,
      y: outerStart.y,
      z: innerStart.z ?? storeyHeight,
      type: POINT_TYPES.WALL_OUTER,
      wallId, // Referenz zur zugehörigen Wand
      linkedToPointId: startPointId, // Bewegt sich mit diesem Innenpunkt
    };

    const outerEndPoint = {
      id: generateId(),
      x: outerEnd.x,
      y: outerEnd.y,
      z: innerEnd.z ?? storeyHeight,
      type: POINT_TYPES.WALL_OUTER,
      wallId,
      linkedToPointId: endPointId,
    };

    // Außenpunkte zum State hinzufügen
    setPoints(prev => [...prev, outerStartPoint, outerEndPoint]);

    const newWall = {
      id: wallId,
      // Innenkante (gezeichnete Linie)
      startPointId,           // = innerStartPointId
      endPointId,             // = innerEndPointId
      // Außenkante (echte Punkte)
      outerStartPointId: outerStartPoint.id,
      outerEndPointId: outerEndPoint.id,
      // Höhen
      startHeight: storeyHeight,
      endHeight: storeyHeight,
      // Wandstärke und Richtung
      thickness,
      flipped,
      // Räume pro Seite (statt spaceIds Array)
      innerSpaceId: isOuterSpace ? null : spaceId,
      outerSpaceId: isOuterSpace ? spaceId : null,
      // Legacy: spaceIds für Abwärtskompatibilität (wird aus inner/outer berechnet)
      spaceIds: spaceId ? [spaceId] : [],
      // Weitere Eigenschaften
      typeRef: null,
      openings: [],
      roomForming: true,
    };

    setWalls(prev => [...prev, newWall]);
    setTimeout(saveSnapshot, 0);
    return newWall;
  }, [findWallBetweenPoints, points, storeyHeight, saveSnapshot]);

  // Wand aktualisieren
  // Wenn thickness oder flipped geändert wird, werden die Außenpunkte neu berechnet
  const updateWall = useCallback((wallId, updates) => {
    const wall = walls.find(w => w.id === wallId);
    if (!wall) return;

    // Prüfen ob thickness oder flipped geändert werden
    const thicknessChanged = updates.thickness !== undefined && updates.thickness !== wall.thickness;
    const flippedChanged = updates.flipped !== undefined && updates.flipped !== wall.flipped;

    // Wand aktualisieren
    setWalls(prev => prev.map(w =>
      w.id === wallId ? { ...w, ...updates } : w
    ));

    // Wenn Geometrie-relevante Eigenschaften geändert wurden: Außenpunkte neu berechnen
    if ((thicknessChanged || flippedChanged) && wall.outerStartPointId && wall.outerEndPointId) {
      const newThickness = updates.thickness ?? wall.thickness ?? 125;
      const newFlipped = updates.flipped ?? wall.flipped ?? false;

      setPoints(prev => {
        const innerStart = prev.find(p => p.id === wall.startPointId);
        const innerEnd = prev.find(p => p.id === wall.endPointId);
        if (!innerStart || !innerEnd) return prev;

        const { outerStart, outerEnd } = calculateOuterPoints(innerStart, innerEnd, newThickness, newFlipped);

        return prev.map(p => {
          if (p.id === wall.outerStartPointId) {
            return { ...p, x: outerStart.x, y: outerStart.y };
          } else if (p.id === wall.outerEndPointId) {
            return { ...p, x: outerEnd.x, y: outerEnd.y };
          }
          return p;
        });
      });
    }

    setTimeout(saveSnapshot, 0);
  }, [walls, saveSnapshot]);

  // Wand löschen (inklusive zugehöriger Außenpunkte)
  const deleteWall = useCallback((wallId) => {
    const wall = walls.find(w => w.id === wallId);

    // Außenpunkte löschen wenn vorhanden
    if (wall?.outerStartPointId || wall?.outerEndPointId) {
      setPoints(prev => prev.filter(p =>
        p.id !== wall.outerStartPointId && p.id !== wall.outerEndPointId
      ));
    }

    setWalls(prev => prev.filter(w => w.id !== wallId));
    setTimeout(saveSnapshot, 0);
  }, [walls, saveSnapshot]);

  // Issue #11: Zwei Wände zusammenführen
  // Wände müssen einen gemeinsamen Punkt haben
  const mergeWalls = useCallback((wallId1, wallId2) => {
    const wall1 = walls.find(w => w.id === wallId1);
    const wall2 = walls.find(w => w.id === wallId2);

    if (!wall1 || !wall2) {
      console.warn('mergeWalls: Eine oder beide Wände nicht gefunden');
      return false;
    }

    // Gemeinsamen Punkt finden
    const wall1Points = [wall1.startPointId, wall1.endPointId];
    const wall2Points = [wall2.startPointId, wall2.endPointId];

    let sharedPointId = null;
    let wall1OtherPoint = null;
    let wall2OtherPoint = null;

    for (const p1 of wall1Points) {
      for (const p2 of wall2Points) {
        if (p1 === p2) {
          sharedPointId = p1;
          wall1OtherPoint = wall1Points.find(p => p !== sharedPointId);
          wall2OtherPoint = wall2Points.find(p => p !== sharedPointId);
          break;
        }
      }
      if (sharedPointId) break;
    }

    if (!sharedPointId) {
      console.warn('mergeWalls: Wände haben keinen gemeinsamen Punkt');
      return false;
    }

    // Neue Wand erstellen (verbindet die beiden Enden)
    const newWall = {
      id: generateId(),
      startPointId: wall1OtherPoint,
      endPointId: wall2OtherPoint,
      thickness: wall1.thickness || wall2.thickness || 125,
      flipped: wall1.flipped || false,
      spaceIds: [...new Set([...(wall1.spaceIds || []), ...(wall2.spaceIds || [])])],
      openings: [...(wall1.openings || []), ...(wall2.openings || [])],
    };

    // Außenpunkte für neue Wand berechnen
    const startPt = points.find(p => p.id === newWall.startPointId);
    const endPt = points.find(p => p.id === newWall.endPointId);

    if (startPt && endPt) {
      const { outerStart, outerEnd } = calculateOuterPoints(startPt, endPt, newWall.thickness, newWall.flipped);

      const outerStartPoint = {
        id: generateId(),
        x: outerStart.x,
        y: outerStart.y,
        z: startPt.z ?? storeyHeight,
        type: POINT_TYPES.WALL_OUTER,
        wallId: newWall.id,
        linkedToPointId: newWall.startPointId,
      };

      const outerEndPoint = {
        id: generateId(),
        x: outerEnd.x,
        y: outerEnd.y,
        z: endPt.z ?? storeyHeight,
        type: POINT_TYPES.WALL_OUTER,
        wallId: newWall.id,
        linkedToPointId: newWall.endPointId,
      };

      newWall.outerStartPointId = outerStartPoint.id;
      newWall.outerEndPointId = outerEndPoint.id;

      // Alte Außenpunkte entfernen, neue hinzufügen
      setPoints(prev => {
        const filtered = prev.filter(p =>
          p.id !== wall1.outerStartPointId &&
          p.id !== wall1.outerEndPointId &&
          p.id !== wall2.outerStartPointId &&
          p.id !== wall2.outerEndPointId
        );
        return [...filtered, outerStartPoint, outerEndPoint];
      });
    }

    // Alte Wände löschen, neue hinzufügen
    setWalls(prev => {
      const filtered = prev.filter(w => w.id !== wallId1 && w.id !== wallId2);
      return [...filtered, newWall];
    });

    // Gemeinsamen Punkt kann man optional löschen (falls nicht von anderen Wänden verwendet)
    // Hier erstmal behalten für Flexibilität

    console.log(`Wände ${wallId1} und ${wallId2} zu ${newWall.id} zusammengeführt`);
    setTimeout(saveSnapshot, 0);
    return newWall.id;
  }, [walls, points, storeyHeight, saveSnapshot]);

  // Issue #11: Wand in Segmente teilen
  // segmentCount = Anzahl der Segmente (ergibt segmentCount-1 neue Punkte)
  // ODER segmentLength = Länge jedes Segments in mm (berechnet Anzahl automatisch)
  const divideWall = useCallback((wallId, { segmentCount = null, segmentLength = null } = {}) => {
    const wall = walls.find(w => w.id === wallId);
    if (!wall) {
      console.warn('divideWall: Wand nicht gefunden');
      return false;
    }

    const startPt = points.find(p => p.id === wall.startPointId);
    const endPt = points.find(p => p.id === wall.endPointId);

    if (!startPt || !endPt) {
      console.warn('divideWall: Start- oder Endpunkt nicht gefunden');
      return false;
    }

    // Wandlänge berechnen
    const dx = endPt.x - startPt.x;
    const dy = endPt.y - startPt.y;
    const wallLength = Math.sqrt(dx * dx + dy * dy);

    // Anzahl Segmente bestimmen
    let numSegments;
    if (segmentCount && segmentCount > 1) {
      numSegments = segmentCount;
    } else if (segmentLength && segmentLength > 0) {
      numSegments = Math.max(2, Math.floor(wallLength / segmentLength));
    } else {
      console.warn('divideWall: Entweder segmentCount oder segmentLength angeben');
      return false;
    }

    if (numSegments < 2) {
      console.warn('divideWall: Mindestens 2 Segmente erforderlich');
      return false;
    }

    // Zwischenpunkte berechnen
    const newPoints = [];
    const newPointIds = [];

    for (let i = 1; i < numSegments; i++) {
      const t = i / numSegments;
      const x = startPt.x + dx * t;
      const y = startPt.y + dy * t;
      // Z-Höhe interpolieren zwischen Start und Ende
      const z = (startPt.z ?? storeyHeight) + ((endPt.z ?? storeyHeight) - (startPt.z ?? storeyHeight)) * t;

      const newPoint = {
        id: generateId(),
        x: Math.round(x),
        y: Math.round(y),
        z,
        type: POINT_TYPES.WALL,
      };
      newPoints.push(newPoint);
      newPointIds.push(newPoint.id);
    }

    // Neue Wände erstellen
    const allPointIds = [wall.startPointId, ...newPointIds, wall.endPointId];
    const newWalls = [];

    for (let i = 0; i < allPointIds.length - 1; i++) {
      const segStartPtId = allPointIds[i];
      const segEndPtId = allPointIds[i + 1];
      const segStartPt = i === 0 ? startPt : newPoints[i - 1];
      const segEndPt = i === allPointIds.length - 2 ? endPt : newPoints[i];

      const newWallId = generateId();
      const { outerStart, outerEnd } = calculateOuterPoints(
        segStartPt, segEndPt,
        wall.thickness || 125,
        wall.flipped || false
      );

      const outerStartPoint = {
        id: generateId(),
        x: outerStart.x,
        y: outerStart.y,
        z: segStartPt.z ?? storeyHeight,
        type: POINT_TYPES.WALL_OUTER,
        wallId: newWallId,
        linkedToPointId: segStartPtId,
      };

      const outerEndPoint = {
        id: generateId(),
        x: outerEnd.x,
        y: outerEnd.y,
        z: segEndPt.z ?? storeyHeight,
        type: POINT_TYPES.WALL_OUTER,
        wallId: newWallId,
        linkedToPointId: segEndPtId,
      };

      newWalls.push({
        id: newWallId,
        startPointId: segStartPtId,
        endPointId: segEndPtId,
        outerStartPointId: outerStartPoint.id,
        outerEndPointId: outerEndPoint.id,
        thickness: wall.thickness || 125,
        flipped: wall.flipped || false,
        roomForming: wall.roomForming ?? true,
        // Öffnungen bleiben an der Originalposition - hier vereinfacht: nur erste Wand bekommt sie
        openings: i === 0 ? (wall.openings || []) : [],
      });

      newPoints.push(outerStartPoint, outerEndPoint);
    }

    // Alte Wand und deren Außenpunkte entfernen
    setPoints(prev => {
      const filtered = prev.filter(p =>
        p.id !== wall.outerStartPointId && p.id !== wall.outerEndPointId
      );
      return [...filtered, ...newPoints];
    });

    setWalls(prev => {
      const filtered = prev.filter(w => w.id !== wallId);
      return [...filtered, ...newWalls];
    });

    console.log(`Wand ${wallId} in ${numSegments} Segmente geteilt (${newWalls.length} neue Wände)`);
    setTimeout(saveSnapshot, 0);
    return newWalls.map(w => w.id);
  }, [walls, points, storeyHeight, saveSnapshot]);

  // ============================================
  // BOGEN (ARC) - 3-Punkt-Bogen erstellen
  // ============================================

  // Hilfsfunktion: Arc-Geometrie generieren (ohne State-Änderungen)
  // pointZ = Z-Koordinate der Punkte (Höhe über Boden)
  // wallHeight = Wandhöhe (normalerweise Geschosshöhe)
  // fixedStartId/fixedEndId: Wenn gesetzt, werden diese IDs für Start/Ende verwendet (keine Suche/Erstellung)
  // Gibt auch existingEndpointIds zurück - IDs von existierenden Punkten die als Arc-Endpoints markiert werden müssen
  const generateArcGeometry = useCallback((startPoint, throughPoint, endPoint, segments, pointZ, wallHeight, arcId, currentPoints, fixedStartId = null, fixedEndId = null) => {
    const arcPoints = generateArcPoints(startPoint, throughPoint, endPoint, segments);
    if (arcPoints.length < 2) return null;

    const newPoints = [];
    const newPointIds = [];
    const existingEndpointIds = []; // IDs von existierenden Punkten, die isArcEndpoint bekommen müssen

    // Startpunkt: Feste ID verwenden, oder existierenden suchen, oder neuen erstellen
    if (fixedStartId) {
      // Feste ID verwenden (für updateArcStart/End wo der Punkt bereits existiert)
      newPointIds.push(fixedStartId);
      existingEndpointIds.push(fixedStartId);
    } else {
      // Existierenden Punkt suchen
      const existingStart = currentPoints.find(p =>
        Math.abs(p.x - arcPoints[0].x) < 50 && Math.abs(p.y - arcPoints[0].y) < 50 && (!p.arcId || p.arcId === arcId)
      );
      if (existingStart) {
        newPointIds.push(existingStart.id);
        existingEndpointIds.push(existingStart.id);
      } else {
        const newPoint = {
          id: generateId(),
          x: arcPoints[0].x,
          y: arcPoints[0].y,
          z: pointZ,
          type: POINT_TYPES.WALL,
          arcId,
          isArcEndpoint: true,
        };
        newPoints.push(newPoint);
        newPointIds.push(newPoint.id);
      }
    }

    // Zwischenpunkte erstellen (alle gehören zum Arc)
    for (let i = 1; i < arcPoints.length - 1; i++) {
      const arcPt = arcPoints[i];
      const newPoint = {
        id: generateId(),
        x: arcPt.x,
        y: arcPt.y,
        z: pointZ,
        type: POINT_TYPES.WALL,
        arcId,
        isArcIntermediate: true,
      };
      newPoints.push(newPoint);
      newPointIds.push(newPoint.id);
    }

    // Endpunkt: Feste ID verwenden, oder existierenden suchen, oder neuen erstellen
    if (fixedEndId) {
      // Feste ID verwenden (für updateArcStart/End wo der Punkt bereits existiert)
      newPointIds.push(fixedEndId);
      existingEndpointIds.push(fixedEndId);
    } else {
      const lastArcPt = arcPoints[arcPoints.length - 1];
      const existingEnd = currentPoints.find(p =>
        Math.abs(p.x - lastArcPt.x) < 50 && Math.abs(p.y - lastArcPt.y) < 50 && (!p.arcId || p.arcId === arcId)
      );
      if (existingEnd) {
        newPointIds.push(existingEnd.id);
        existingEndpointIds.push(existingEnd.id);
      } else {
        const newPoint = {
          id: generateId(),
          x: lastArcPt.x,
          y: lastArcPt.y,
          z: pointZ,
          type: POINT_TYPES.WALL,
          arcId,
          isArcEndpoint: true,
        };
        newPoints.push(newPoint);
        newPointIds.push(newPoint.id);
      }
    }

    // Wände zwischen aufeinanderfolgenden Punkten erstellen
    const newWalls = [];
    for (let i = 0; i < newPointIds.length - 1; i++) {
      newWalls.push({
        id: generateId(),
        startPointId: newPointIds[i],
        endPointId: newPointIds[i + 1],
        startHeight: wallHeight,
        endHeight: wallHeight,
        thickness: 125,
        flipped: false,
        spaceIds: [],
        typeRef: null,
        openings: [],
        roomForming: true,
        isArcSegment: true,
        arcId,
      });
    }

    return { newPoints, newPointIds, newWalls, existingEndpointIds };
  }, []);

  // Bogen aus 3 Punkten erstellen (Start, Durchgang, Ende)
  // Speichert Arc-Metadaten für spätere Bearbeitung
  // zHeight: Z-Koordinate der Punkte (Höhe über Boden, z.B. 0 für Bodenpunkte)
  // Die Wandhöhe ist immer storeyHeight!
  const createArc = useCallback((startPoint, throughPoint, endPoint, segments = 8, zHeight = null) => {
    const pointZ = zHeight !== null ? zHeight : storeyHeight;
    const arcId = generateId();

    // Geometrie generieren
    // pointZ = Z-Koordinate der Punkte
    // storeyHeight = Wandhöhe (Geschosshöhe)
    const geometry = generateArcGeometry(startPoint, throughPoint, endPoint, segments, pointZ, storeyHeight, arcId, points);
    if (!geometry) return null;

    const { newPoints, newPointIds, newWalls, existingEndpointIds } = geometry;

    // Through-Point (Scheitelpunkt): Prüfen ob bereits ein Punkt an dieser Position existiert
    // (z.B. wenn der Benutzer auf einen existierenden Punkt geklickt hat)
    const existingThroughPoint = points.find(p =>
      Math.abs(p.x - throughPoint.x) < 50 && Math.abs(p.y - throughPoint.y) < 50 &&
      !p.arcId // Nicht bereits Teil eines anderen Bogens
    );

    let throughPointId;
    let throughPointToAdd = null;
    let existingThroughPointToUpdate = null;

    if (existingThroughPoint) {
      // Existierenden Punkt als Through-Point verwenden
      throughPointId = existingThroughPoint.id;
      existingThroughPointToUpdate = existingThroughPoint.id;
    } else {
      // Neuen Through-Point erstellen
      const throughPointObj = {
        id: generateId(),
        x: throughPoint.x,
        y: throughPoint.y,
        z: pointZ,
        type: POINT_TYPES.WALL,
        arcId,
        isArcThroughPoint: true,
      };
      throughPointId = throughPointObj.id;
      throughPointToAdd = throughPointObj;
    }

    // Arc-Metadaten speichern
    const arcData = {
      id: arcId,
      startPointId: newPointIds[0],
      endPointId: newPointIds[newPointIds.length - 1],
      throughPointId,
      segments,
      zHeight: pointZ,
      wallHeight: storeyHeight, // Wandhöhe separat speichern
      wallIds: newWalls.map(w => w.id),
      intermediatePointIds: newPointIds.slice(1, -1), // Punkte zwischen Start und End
    };

    // State aktualisieren
    // Existierende Endpunkte mit isArcEndpoint und arcId markieren
    const existingEndpointSet = new Set(existingEndpointIds);
    setPoints(prev => {
      let updated = prev.map(p => {
        // Existierenden Through-Point markieren
        if (existingThroughPointToUpdate && p.id === existingThroughPointToUpdate) {
          return { ...p, arcId, isArcThroughPoint: true };
        }
        // Existierende Endpunkte markieren
        if (existingEndpointSet.has(p.id)) {
          return { ...p, arcId, isArcEndpoint: true };
        }
        return p;
      });
      // Neue Punkte hinzufügen (Arc-Punkte und ggf. neuer Through-Point)
      if (throughPointToAdd) {
        return [...updated, ...newPoints, throughPointToAdd];
      }
      return [...updated, ...newPoints];
    });
    setWalls(prev => [...prev, ...newWalls]);
    setArcs(prev => [...prev, arcData]);
    setTimeout(saveSnapshot, 0);

    return { arcId, pointIds: newPointIds, walls: newWalls, throughPointId };
  }, [points, storeyHeight, saveSnapshot, generateArcGeometry]);

  // Bogen aktualisieren wenn Through-Punkt verschoben wird
  const updateArc = useCallback((arcId, newThroughX, newThroughY) => {
    const arc = arcs.find(a => a.id === arcId);
    if (!arc) return null;

    // Start- und Endpunkt holen
    const startPoint = points.find(p => p.id === arc.startPointId);
    const endPoint = points.find(p => p.id === arc.endPointId);
    if (!startPoint || !endPoint) return null;

    const newThroughPoint = { x: newThroughX, y: newThroughY };

    // Alte Zwischenpunkte und Wände entfernen
    const oldWallIds = new Set(arc.wallIds);
    const oldIntermediateIds = new Set(arc.intermediatePointIds);

    // Wandhöhe aus Arc-Metadaten oder Fallback auf storeyHeight
    const wallHeight = arc.wallHeight || storeyHeight;

    // Neue Geometrie generieren
    // WICHTIG: Feste IDs für Start und Ende übergeben, damit keine Duplikate entstehen
    const geometry = generateArcGeometry(
      startPoint,
      newThroughPoint,
      endPoint,
      arc.segments,
      arc.zHeight,
      wallHeight,
      arcId,
      points.filter(p => !oldIntermediateIds.has(p.id)),
      arc.startPointId,  // Feste Start-ID
      arc.endPointId     // Feste End-ID
    );

    if (!geometry) return null;

    const { newPoints, newPointIds, newWalls } = geometry;

    // Through-Punkt aktualisieren + Endpunkte sicherstellen
    setPoints(prev => {
      const filtered = prev.filter(p => !oldIntermediateIds.has(p.id));
      const updated = filtered.map(p => {
        if (p.id === arc.throughPointId) {
          return { ...p, x: newThroughX, y: newThroughY };
        }
        if (p.id === arc.startPointId || p.id === arc.endPointId) {
          return { ...p, arcId, isArcEndpoint: true };
        }
        return p;
      });
      return [...updated, ...newPoints.filter(np => !updated.some(p => p.id === np.id))];
    });

    // Alte Wände entfernen, neue hinzufügen
    setWalls(prev => {
      const filtered = prev.filter(w => !oldWallIds.has(w.id));
      return [...filtered, ...newWalls];
    });

    // Arc-Metadaten aktualisieren
    setArcs(prev => prev.map(a =>
      a.id === arcId
        ? {
            ...a,
            wallIds: newWalls.map(w => w.id),
            intermediatePointIds: newPointIds.slice(1, -1),
          }
        : a
    ));

    setTimeout(saveSnapshot, 0);
    return { arcId, pointIds: newPointIds, walls: newWalls };
  }, [arcs, points, storeyHeight, generateArcGeometry, saveSnapshot]);

  // Prüfen ob ein Punkt ein Arc-Through-Point ist
  const getArcForThroughPoint = useCallback((pointId) => {
    return arcs.find(a => a.throughPointId === pointId);
  }, [arcs]);

  // Prüfen ob ein Punkt ein Arc-Start-Punkt ist
  const getArcForStartPoint = useCallback((pointId) => {
    return arcs.find(a => a.startPointId === pointId);
  }, [arcs]);

  // Prüfen ob ein Punkt ein Arc-End-Punkt ist
  const getArcForEndPoint = useCallback((pointId) => {
    return arcs.find(a => a.endPointId === pointId);
  }, [arcs]);

  // Prüfen ob ein Punkt zu irgendeinem Arc gehört (als Start, End oder Through)
  const getArcForPoint = useCallback((pointId) => {
    return arcs.find(a =>
      a.startPointId === pointId ||
      a.endPointId === pointId ||
      a.throughPointId === pointId
    );
  }, [arcs]);

  // Hilfsfunktion: Berechne neuen Through-Point wenn Start oder Ende sich ändert
  // Hält den Through-Point auf der Mittelsenkrechten der neuen Sehne im gleichen Abstand
  const calculateNewThroughPoint = useCallback((oldStart, oldEnd, oldThrough, newStart, newEnd) => {
    // Alte Sehne: Mittelpunkt
    const oldMidX = (oldStart.x + oldEnd.x) / 2;
    const oldMidY = (oldStart.y + oldEnd.y) / 2;

    // Abstand des Through-Points von der alten Sehnenmitte (in Richtung der Mittelsenkrechten)
    // Vektor von Mitte zu Through
    const toThroughX = oldThrough.x - oldMidX;
    const toThroughY = oldThrough.y - oldMidY;

    // Normale zur alten Sehne (Mittelsenkrechte)
    const oldDx = oldEnd.x - oldStart.x;
    const oldDy = oldEnd.y - oldStart.y;
    // Normalisierte Normale (90° gedreht)
    const oldNormLen = Math.hypot(oldDx, oldDy);
    if (oldNormLen < 1) return oldThrough; // Sehne zu kurz
    const oldNormX = -oldDy / oldNormLen;
    const oldNormY = oldDx / oldNormLen;

    // Abstand in Normalenrichtung (mit Vorzeichen)
    const heightDist = toThroughX * oldNormX + toThroughY * oldNormY;

    // Neue Sehne
    const newMidX = (newStart.x + newEnd.x) / 2;
    const newMidY = (newStart.y + newEnd.y) / 2;
    const newDx = newEnd.x - newStart.x;
    const newDy = newEnd.y - newStart.y;
    const newNormLen = Math.hypot(newDx, newDy);
    if (newNormLen < 1) return oldThrough;

    // Neue Normale
    const newNormX = -newDy / newNormLen;
    const newNormY = newDx / newNormLen;

    // Neuer Through-Point: Sehnenmitte + Abstand in Normalenrichtung
    return {
      x: newMidX + heightDist * newNormX,
      y: newMidY + heightDist * newNormY,
    };
  }, []);

  // Arc mit neuem Start-Punkt aktualisieren (End bleibt, Through wird proportional verschoben)
  const updateArcStart = useCallback((arcId, newStartX, newStartY) => {
    const arc = arcs.find(a => a.id === arcId);
    if (!arc) return null;

    // Alle drei Punkte holen
    const oldStartPoint = points.find(p => p.id === arc.startPointId);
    const throughPoint = points.find(p => p.id === arc.throughPointId);
    const endPoint = points.find(p => p.id === arc.endPointId);
    if (!oldStartPoint || !throughPoint || !endPoint) return null;

    const newStartPoint = { x: newStartX, y: newStartY };

    // Through-Point proportional verschieben
    const newThroughPoint = calculateNewThroughPoint(
      oldStartPoint, endPoint, throughPoint,
      newStartPoint, endPoint
    );

    // Alte Wände und Zwischenpunkte entfernen
    const oldWallIds = new Set(arc.wallIds);
    const oldIntermediateIds = new Set(arc.intermediatePointIds);

    const wallHeight = arc.wallHeight || storeyHeight;

    // Neue Geometrie mit neuem Startpunkt und verschobenem Through-Point generieren
    // WICHTIG: Feste IDs für Start und Ende übergeben, damit keine Duplikate entstehen
    const geometry = generateArcGeometry(
      newStartPoint,
      newThroughPoint,
      endPoint,
      arc.segments,
      arc.zHeight,
      wallHeight,
      arcId,
      points.filter(p => !oldIntermediateIds.has(p.id)),
      arc.startPointId,  // Feste Start-ID
      arc.endPointId     // Feste End-ID
    );

    if (!geometry) return null;

    const { newPoints, newPointIds, newWalls } = geometry;

    // Start-Punkt und Through-Punkt aktualisieren
    setPoints(prev => {
      const filtered = prev.filter(p => !oldIntermediateIds.has(p.id));
      const updated = filtered.map(p => {
        if (p.id === arc.startPointId) {
          return { ...p, x: newStartX, y: newStartY, arcId, isArcEndpoint: true };
        }
        if (p.id === arc.throughPointId) {
          return { ...p, x: newThroughPoint.x, y: newThroughPoint.y };
        }
        if (p.id === arc.endPointId) {
          return { ...p, arcId, isArcEndpoint: true };
        }
        return p;
      });
      return [...updated, ...newPoints.filter(np => !updated.some(p => p.id === np.id))];
    });

    // Alte Wände entfernen, neue hinzufügen
    setWalls(prev => {
      const filtered = prev.filter(w => !oldWallIds.has(w.id));
      return [...filtered, ...newWalls];
    });

    // Arc-Metadaten aktualisieren
    setArcs(prev => prev.map(a =>
      a.id === arcId
        ? {
            ...a,
            wallIds: newWalls.map(w => w.id),
            intermediatePointIds: newPointIds.slice(1, -1),
          }
        : a
    ));

    setTimeout(saveSnapshot, 0);
    return { arcId, pointIds: newPointIds, walls: newWalls };
  }, [arcs, points, storeyHeight, generateArcGeometry, saveSnapshot, calculateNewThroughPoint]);

  // Arc mit neuem End-Punkt aktualisieren (Start bleibt, Through wird proportional verschoben)
  const updateArcEnd = useCallback((arcId, newEndX, newEndY) => {
    const arc = arcs.find(a => a.id === arcId);
    if (!arc) return null;

    // Alle drei Punkte holen
    const startPoint = points.find(p => p.id === arc.startPointId);
    const throughPoint = points.find(p => p.id === arc.throughPointId);
    const oldEndPoint = points.find(p => p.id === arc.endPointId);
    if (!startPoint || !throughPoint || !oldEndPoint) return null;

    const newEndPoint = { x: newEndX, y: newEndY };

    // Through-Point proportional verschieben
    const newThroughPoint = calculateNewThroughPoint(
      startPoint, oldEndPoint, throughPoint,
      startPoint, newEndPoint
    );

    // Alte Wände und Zwischenpunkte entfernen
    const oldWallIds = new Set(arc.wallIds);
    const oldIntermediateIds = new Set(arc.intermediatePointIds);

    const wallHeight = arc.wallHeight || storeyHeight;

    // Neue Geometrie mit neuem Endpunkt und verschobenem Through-Point generieren
    // WICHTIG: Feste IDs für Start und Ende übergeben, damit keine Duplikate entstehen
    const geometry = generateArcGeometry(
      startPoint,
      newThroughPoint,
      newEndPoint,
      arc.segments,
      arc.zHeight,
      wallHeight,
      arcId,
      points.filter(p => !oldIntermediateIds.has(p.id)),
      arc.startPointId,  // Feste Start-ID
      arc.endPointId     // Feste End-ID
    );

    if (!geometry) return null;

    const { newPoints, newPointIds, newWalls } = geometry;

    // End-Punkt und Through-Punkt aktualisieren
    setPoints(prev => {
      const filtered = prev.filter(p => !oldIntermediateIds.has(p.id));
      const updated = filtered.map(p => {
        if (p.id === arc.endPointId) {
          return { ...p, x: newEndX, y: newEndY, arcId, isArcEndpoint: true };
        }
        if (p.id === arc.throughPointId) {
          return { ...p, x: newThroughPoint.x, y: newThroughPoint.y };
        }
        if (p.id === arc.startPointId) {
          return { ...p, arcId, isArcEndpoint: true };
        }
        return p;
      });
      return [...updated, ...newPoints.filter(np => !updated.some(p => p.id === np.id))];
    });

    // Alte Wände entfernen, neue hinzufügen
    setWalls(prev => {
      const filtered = prev.filter(w => !oldWallIds.has(w.id));
      return [...filtered, ...newWalls];
    });

    // Arc-Metadaten aktualisieren
    setArcs(prev => prev.map(a =>
      a.id === arcId
        ? {
            ...a,
            wallIds: newWalls.map(w => w.id),
            intermediatePointIds: newPointIds.slice(1, -1),
          }
        : a
    ));

    setTimeout(saveSnapshot, 0);
    return { arcId, pointIds: newPointIds, walls: newWalls };
  }, [arcs, points, storeyHeight, generateArcGeometry, saveSnapshot, calculateNewThroughPoint]);

  // Arc-Segmentanzahl ändern
  const updateArcSegments = useCallback((arcId, newSegments) => {
    const arc = arcs.find(a => a.id === arcId);
    if (!arc) return null;

    // Alle drei Kontrollpunkte holen
    const startPoint = points.find(p => p.id === arc.startPointId);
    const throughPoint = points.find(p => p.id === arc.throughPointId);
    const endPoint = points.find(p => p.id === arc.endPointId);
    if (!startPoint || !throughPoint || !endPoint) return null;

    // Alte Wände und Zwischenpunkte entfernen
    const oldWallIds = new Set(arc.wallIds);
    const oldIntermediateIds = new Set(arc.intermediatePointIds);

    const wallHeight = arc.wallHeight || storeyHeight;

    // Neue Geometrie mit neuer Segmentanzahl generieren
    // WICHTIG: Feste IDs für Start und Ende übergeben, damit keine Duplikate entstehen
    const geometry = generateArcGeometry(
      startPoint,
      throughPoint,
      endPoint,
      newSegments,
      arc.zHeight,
      wallHeight,
      arcId,
      points.filter(p => !oldIntermediateIds.has(p.id)),
      arc.startPointId,  // Feste Start-ID
      arc.endPointId     // Feste End-ID
    );

    if (!geometry) return null;

    const { newPoints, newPointIds, newWalls } = geometry;

    // Punkte aktualisieren (alte Zwischenpunkte entfernen, neue hinzufügen, Endpunkte sicherstellen)
    setPoints(prev => {
      const filtered = prev.filter(p => !oldIntermediateIds.has(p.id));
      const updated = filtered.map(p =>
        (p.id === arc.startPointId || p.id === arc.endPointId)
          ? { ...p, arcId, isArcEndpoint: true }
          : p
      );
      return [...updated, ...newPoints.filter(np => !updated.some(p => p.id === np.id))];
    });

    // Alte Wände entfernen, neue hinzufügen
    setWalls(prev => {
      const filtered = prev.filter(w => !oldWallIds.has(w.id));
      return [...filtered, ...newWalls];
    });

    // Arc-Metadaten aktualisieren (inkl. neuer Segmentanzahl)
    setArcs(prev => prev.map(a =>
      a.id === arcId
        ? {
            ...a,
            segments: newSegments,
            wallIds: newWalls.map(w => w.id),
            intermediatePointIds: newPointIds.slice(1, -1),
          }
        : a
    ));

    setTimeout(saveSnapshot, 0);
    return { arcId, pointIds: newPointIds, walls: newWalls };
  }, [arcs, points, storeyHeight, generateArcGeometry, saveSnapshot]);

  // Arc löschen (alle Punkte und Wände)
  const deleteArc = useCallback((arcId) => {
    const arc = arcs.find(a => a.id === arcId);
    if (!arc) return false;

    // Through-Punkt und Zwischenpunkte entfernen
    const pointsToRemove = new Set([arc.throughPointId, ...arc.intermediatePointIds]);
    setPoints(prev => prev.filter(p => !pointsToRemove.has(p.id)));

    // Wände entfernen
    const wallsToRemove = new Set(arc.wallIds);
    setWalls(prev => prev.filter(w => !wallsToRemove.has(w.id)));

    // Arc-Metadaten entfernen
    setArcs(prev => prev.filter(a => a.id !== arcId));

    setTimeout(saveSnapshot, 0);
    return true;
  }, [arcs, saveSnapshot]);

  // Arc "explodieren" - in unabhängige Wandsegmente konvertieren
  // Die Wände und Punkte bleiben erhalten, verlieren aber ihre Arc-Zugehörigkeit
  // Der Through-Point wird optional gelöscht (keepThroughPoint = false) oder als Hilfspunkt behalten
  const explodeArc = useCallback((arcId, keepThroughPoint = false) => {
    const arc = arcs.find(a => a.id === arcId);
    if (!arc) return false;

    // Alle Arc-bezogenen Punkte sammeln
    const arcPointIds = new Set([
      arc.startPointId,
      arc.endPointId,
      arc.throughPointId,
      ...arc.intermediatePointIds,
    ]);

    // Punkte aktualisieren: Arc-Flags entfernen
    setPoints(prev => {
      const updated = prev.map(p => {
        if (!arcPointIds.has(p.id)) return p;

        // Through-Point optional löschen oder als Hilfspunkt behalten
        if (p.id === arc.throughPointId && !keepThroughPoint) {
          return null; // Wird unten gefiltert
        }

        // Arc-Flags entfernen, Punkt wird zu normalem Wandpunkt
        const { arcId: _, isArcThroughPoint, isArcEndpoint, isArcIntermediate, ...rest } = p;

        // Through-Point wird zu Hilfspunkt wenn behalten
        if (p.id === arc.throughPointId && keepThroughPoint) {
          return { ...rest, type: POINT_TYPES.WALL };
        }

        return rest;
      });

      return updated.filter(Boolean);
    });

    // Wände aktualisieren: Arc-Flags entfernen
    const arcWallIds = new Set(arc.wallIds);
    setWalls(prev => prev.map(w => {
      if (!arcWallIds.has(w.id)) return w;

      // Arc-Flags entfernen
      const { isArcSegment, arcId: _, ...rest } = w;
      return rest;
    }));

    // Arc-Metadaten entfernen
    setArcs(prev => prev.filter(a => a.id !== arcId));

    setTimeout(saveSnapshot, 0);
    return true;
  }, [arcs, saveSnapshot]);

  // Öffnung zu Wand hinzufügen
  const addOpeningToWall = useCallback((wallId, opening = {}) => {
    const newOpening = {
      id: generateId(),
      type: opening.type || 'window', // 'window' oder 'door'
      width: opening.width || 1000,
      height: opening.height || 1200,
      position: opening.position || 0.5, // 0-1 entlang der Wand
      sillHeight: opening.sillHeight || 900, // Brüstungshöhe (nur Fenster)
      name: opening.name || '',
    };

    setWalls(prev => prev.map(w =>
      w.id === wallId
        ? { ...w, openings: [...(w.openings || []), newOpening] }
        : w
    ));

    setTimeout(saveSnapshot, 0);
    return newOpening;
  }, [saveSnapshot]);

  // Öffnung aktualisieren
  const updateOpening = useCallback((wallId, openingId, updates) => {
    setWalls(prev => prev.map(w =>
      w.id === wallId
        ? {
            ...w,
            openings: (w.openings || []).map(o =>
              o.id === openingId ? { ...o, ...updates } : o
            ),
          }
        : w
    ));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // Öffnung löschen
  const deleteOpening = useCallback((wallId, openingId) => {
    setWalls(prev => prev.map(w =>
      w.id === wallId
        ? { ...w, openings: (w.openings || []).filter(o => o.id !== openingId) }
        : w
    ));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // Raum teilen, wenn eine Wand zwischen zwei Punkten auf der Grenze gezogen wird
  const splitSpaceWithWall = useCallback((startPointId, endPointId) => {
    console.log('splitSpaceWithWall called:', { startPointId, endPointId });

    // Finde Räume, die beide Punkte in ihrer Grenze haben
    const spacesToSplit = spaces.filter(space => {
      const pids = space.pointIds || [];
      const hasStart = pids.includes(startPointId);
      const hasEnd = pids.includes(endPointId);
      console.log('Checking space:', {
        spaceId: space.id,
        spaceName: space.name,
        pointIds: pids,
        hasStart,
        hasEnd
      });
      return hasStart && hasEnd;
    });

    console.log('Spaces to split:', spacesToSplit.map(s => ({ id: s.id, name: s.name })));

    if (spacesToSplit.length === 0) {
      console.log('No spaces found containing both points');
      return null;
    }

    const results = [];

    for (const space of spacesToSplit) {
      const pointIds = [...(space.pointIds || [])];
      const startIdx = pointIds.indexOf(startPointId);
      const endIdx = pointIds.indexOf(endPointId);

      // Überspringe wenn die Punkte direkt benachbart sind (das ist eine bestehende Kante)
      const diff = Math.abs(startIdx - endIdx);
      if (diff === 1 || diff === pointIds.length - 1) {
        continue; // Benachbarte Punkte = existierende Wand, kein Split
      }

      // Zwei neue Polygone erstellen
      // Polygon 1: Von startIdx zu endIdx (vorwärts)
      // Polygon 2: Von endIdx zu startIdx (vorwärts, also der Rest)

      const polygon1Ids = [];
      const polygon2Ids = [];

      // Polygon 1: startIdx -> endIdx
      let i = startIdx;
      while (true) {
        polygon1Ids.push(pointIds[i]);
        if (i === endIdx) break;
        i = (i + 1) % pointIds.length;
      }

      // Polygon 2: endIdx -> startIdx
      i = endIdx;
      while (true) {
        polygon2Ids.push(pointIds[i]);
        if (i === startIdx) break;
        i = (i + 1) % pointIds.length;
      }

      // Nur splitten wenn beide Polygone mindestens 3 Punkte haben
      if (polygon1Ids.length < 3 || polygon2Ids.length < 3) {
        continue;
      }

      // Neue Räume erstellen
      const space1Id = generateId();
      const space2Id = generateId();

      const newSpace1 = {
        id: space1Id,
        name: `${space.name || 'Raum'} A`,
        pointIds: polygon1Ids,
        colorIndex: space.colorIndex,
      };

      const newSpace2 = {
        id: space2Id,
        name: `${space.name || 'Raum'} B`,
        pointIds: polygon2Ids,
        colorIndex: (space.colorIndex + 1) % 8,
      };

      results.push({
        originalSpace: space,
        newSpaces: [newSpace1, newSpace2],
      });
    }

    if (results.length === 0) return null;

    // Spaces aktualisieren: alte entfernen, neue hinzufügen
    setSpaces(prev => {
      const oldIds = results.map(r => r.originalSpace.id);
      const filtered = prev.filter(s => !oldIds.includes(s.id));
      const newSpaces = results.flatMap(r => r.newSpaces);
      return [...filtered, ...newSpaces];
    });

    // Wände aktualisieren: alte spaceIds durch neue ersetzen
    setWalls(prev => prev.map(wall => {
      let newSpaceIds = [...(wall.spaceIds || [])];

      for (const result of results) {
        const oldId = result.originalSpace.id;
        if (newSpaceIds.includes(oldId)) {
          // Entferne alte ID
          newSpaceIds = newSpaceIds.filter(id => id !== oldId);

          // Füge neue IDs hinzu, wenn die Wand zum jeweiligen Raum gehört
          const [space1, space2] = result.newSpaces;

          // Prüfe ob die Wand zum neuen Raum gehört
          const wallPointIds = [wall.startPointId, wall.endPointId];

          // Wand gehört zu space1 wenn beide Punkte in space1.pointIds und benachbart
          if (isWallInSpace(wallPointIds, space1.pointIds)) {
            newSpaceIds.push(space1.id);
          }
          if (isWallInSpace(wallPointIds, space2.pointIds)) {
            newSpaceIds.push(space2.id);
          }
        }
      }

      return { ...wall, spaceIds: newSpaceIds };
    }));

    setTimeout(saveSnapshot, 0);
    return results;
  }, [spaces, saveSnapshot]);

  // Hilfsfunktion: Prüfen ob eine Wand (zwei Punkte) zu einem Raum gehört
  const isWallInSpace = (wallPointIds, spacePointIds) => {
    const [p1, p2] = wallPointIds;
    const idx1 = spacePointIds.indexOf(p1);
    const idx2 = spacePointIds.indexOf(p2);

    if (idx1 === -1 || idx2 === -1) return false;

    // Punkte müssen benachbart sein im Polygon
    const diff = Math.abs(idx1 - idx2);
    return diff === 1 || diff === spacePointIds.length - 1;
  };

  // Wand hinzufügen und ggf. Räume teilen (für manuell gezeichnete Trennwände)
  const addDividingWall = useCallback((startPointId, endPointId) => {
    // Erst prüfen ob Räume geteilt werden müssen
    const splitResult = splitSpaceWithWall(startPointId, endPointId);

    // Dann Wand erstellen (ohne spaceId - wird durch Split zugewiesen)
    const existing = findWallBetweenPoints(startPointId, endPointId);

    if (existing) {
      // Wand existiert bereits - nur spaceIds aktualisieren wenn gesplittet wurde
      if (splitResult) {
        const newSpaceIds = [];
        for (const result of splitResult) {
          for (const newSpace of result.newSpaces) {
            // Die neue Wand gehört zu beiden neuen Räumen
            newSpaceIds.push(newSpace.id);
          }
        }
        setWalls(prev => prev.map(w =>
          w.id === existing.id
            ? { ...w, spaceIds: [...new Set([...(w.spaceIds || []), ...newSpaceIds])] }
            : w
        ));
      }
      return { wall: existing, splitResult };
    }

    // Neue Wand erstellen
    const newWall = {
      id: generateId(),
      startPointId,
      endPointId,
      startHeight: storeyHeight, // Höhe am Startpunkt (mm)
      endHeight: storeyHeight,   // Höhe am Endpunkt (mm)
      thickness: 125,            // Wandstärke in mm (Standard 12,5cm)
      flipped: false,            // Wenn true: Wandstärke springt nach rechts statt links
      spaceIds: splitResult
        ? splitResult.flatMap(r => r.newSpaces.map(s => s.id))
        : [],
      typeRef: null,
      openings: [],
      roomForming: true, // Raumbildende Wand (false = Wannenverkleidung etc.)
    };

    setWalls(prev => [...prev, newWall]);
    return { wall: newWall, splitResult };
  }, [splitSpaceWithWall, findWallBetweenPoints, storeyHeight]);

  // Neuen Raum erstellen aus Punkt-IDs
  const addSpace = useCallback((pointIds, name) => {
    const spaceId = generateId();

    // Wände für alle Kanten erstellen/aktualisieren
    for (let i = 0; i < pointIds.length; i++) {
      const startId = pointIds[i];
      const endId = pointIds[(i + 1) % pointIds.length];
      addWall(startId, endId, spaceId);
    }

    const newSpace = {
      id: spaceId,
      name: name || `Raum ${spaces.length + 1}`,
      pointIds,
      colorIndex: spaces.length % 8,
      // Hinweis: Boden/Decke werden auf Geschoss-Ebene definiert (storey.ceilingTypeRef)
    };

    setSpaces(prev => [...prev, newSpace]);
    setTimeout(saveSnapshot, 0);
    return newSpace;
  }, [spaces.length, addWall, saveSnapshot]);

  // Raum aktualisieren
  const updateSpace = useCallback((spaceId, updates) => {
    setSpaces(prev => prev.map(s =>
      s.id === spaceId ? { ...s, ...updates } : s
    ));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // Raum löschen
  const deleteSpace = useCallback((spaceId) => {
    // Wände aktualisieren (spaceId entfernen, aber Wände behalten)
    setWalls(prev => prev.map(w => ({
      ...w,
      spaceIds: (w.spaceIds || []).filter(id => id !== spaceId)
    })));

    setSpaces(prev => prev.filter(s => s.id !== spaceId));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // ============================================
  // SCHÄCHTE (Shafts) - Vertikale Durchbrüche
  // ============================================

  // Hilfsfunktion: Finde Wände die an einem Polygon angrenzen (Issue #10)
  const findAdjacentWalls = useCallback((polygonPointIds) => {
    const adjacentWallIds = [];

    // Für jede Kante des Polygons
    for (let i = 0; i < polygonPointIds.length; i++) {
      const edgeStartId = polygonPointIds[i];
      const edgeEndId = polygonPointIds[(i + 1) % polygonPointIds.length];

      // Prüfe alle Wände ob sie diese Kante teilen
      for (const wall of walls) {
        // Wand teilt Kante wenn beide Punkte übereinstimmen (in beliebiger Reihenfolge)
        const wallPointIds = [wall.startPointId, wall.endPointId];
        if (
          (wallPointIds.includes(edgeStartId) && wallPointIds.includes(edgeEndId)) ||
          // Oder Wand endet/beginnt an einem Schacht-Punkt
          wallPointIds.includes(edgeStartId) ||
          wallPointIds.includes(edgeEndId)
        ) {
          if (!adjacentWallIds.includes(wall.id)) {
            adjacentWallIds.push(wall.id);
          }
        }
      }
    }

    return adjacentWallIds;
  }, [walls]);

  // Neuen Schacht erstellen aus Punkt-IDs
  const addShaft = useCallback((pointIds, name) => {
    const shaftId = generateId();

    // Issue #10: Automatisch angrenzende Wände ermitteln
    const adjacentWallIds = findAdjacentWalls(pointIds);

    const newShaft = {
      id: shaftId,
      name: name || `Schacht ${shafts.length + 1}`,
      pointIds,
      adjacentWallIds, // Für Wandabzugsberechnung
      colorIndex: shafts.length % 8,
    };

    setShafts(prev => [...prev, newShaft]);
    setTimeout(saveSnapshot, 0);
    return newShaft;
  }, [shafts.length, findAdjacentWalls, saveSnapshot]);

  // Schacht aktualisieren
  const updateShaft = useCallback((shaftId, updates) => {
    setShafts(prev => prev.map(s =>
      s.id === shaftId ? { ...s, ...updates } : s
    ));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // Schacht löschen
  const deleteShaft = useCallback((shaftId) => {
    setShafts(prev => prev.filter(s => s.id !== shaftId));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // Schacht-Polygon aus pointIds
  const getShaftPolygon = useCallback((shaft) => {
    return (shaft.pointIds || [])
      .map(pid => points.find(p => p.id === pid))
      .filter(Boolean);
  }, [points]);

  // ============================================
  // VORWÄNDE (Stub Walls) - Halbhohe Wände (Polygon-basiert für U-Formen etc.)
  // ============================================

  // Neue Vorwand erstellen aus Punkt-IDs (wie Schacht, aber mit Höhe)
  const addStubWall = useCallback((pointIds, name) => {
    const stubWallId = generateId();

    // Issue #10: Automatisch angrenzende/hinterliegende Wände ermitteln
    const adjacentWallIds = findAdjacentWalls(pointIds);

    const newStubWall = {
      id: stubWallId,
      name: name || `Vorwand ${stubWalls.length + 1}`,
      pointIds, // Polygon-Definition (kann U-Form, L-Form etc. sein)
      height: 1200, // Höhe in mm (typisch für Installationsvorwand)
      adjacentWallIds, // Für Wandabzugsberechnung
      colorIndex: stubWalls.length % 4,
    };

    setStubWalls(prev => [...prev, newStubWall]);
    setTimeout(saveSnapshot, 0);
    return newStubWall;
  }, [stubWalls.length, findAdjacentWalls, saveSnapshot]);

  // Vorwand aktualisieren
  const updateStubWall = useCallback((stubWallId, updates) => {
    setStubWalls(prev => prev.map(sw =>
      sw.id === stubWallId ? { ...sw, ...updates } : sw
    ));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // Vorwand löschen
  const deleteStubWall = useCallback((stubWallId) => {
    setStubWalls(prev => prev.filter(sw => sw.id !== stubWallId));
    setTimeout(saveSnapshot, 0);
  }, [saveSnapshot]);

  // Vorwand-Polygon aus pointIds
  const getStubWallPolygon = useCallback((stubWall) => {
    return (stubWall.pointIds || [])
      .map(pid => points.find(p => p.id === pid))
      .filter(Boolean);
  }, [points]);

  // Umfang eines Polygons berechnen (für Wandfläche)
  const getPolygonPerimeter = useCallback((polygon) => {
    if (polygon.length < 2) return 0;
    let perimeter = 0;
    for (let i = 0; i < polygon.length; i++) {
      const p1 = polygon[i];
      const p2 = polygon[(i + 1) % polygon.length];
      perimeter += distance(p1, p2);
    }
    return perimeter;
  }, []);

  // Vorwand-Wandfläche berechnen (Umfang × Höhe) in m²
  const getStubWallArea = useCallback((stubWall) => {
    const polygon = getStubWallPolygon(stubWall);
    const perimeter = getPolygonPerimeter(polygon);
    // Wandfläche = Umfang × Höhe (in m²)
    return (perimeter / 1000) * (stubWall.height / 1000);
  }, [getStubWallPolygon, getPolygonPerimeter]);

  // ============================================
  // SCHACHT/VORWAND-ABZÜGE FÜR WÄNDE
  // ============================================

  // Berechne Schacht-Abzugsfläche für eine Wand (in mm²)
  // Schächte werden bei voller Wandhöhe abgezogen
  const getShaftDeductionForWall = useCallback((wall, shaft) => {
    const wallStart = points.find(p => p.id === wall.startPointId);
    const wallEnd = points.find(p => p.id === wall.endPointId);
    if (!wallStart || !wallEnd) return 0;

    const shaftPolygon = getShaftPolygon(shaft);
    if (shaftPolygon.length < 2) return 0;

    // Prüfe jede Kante des Schacht-Polygons
    let totalOverlap = 0;
    for (let i = 0; i < shaftPolygon.length; i++) {
      const edgeStart = shaftPolygon[i];
      const edgeEnd = shaftPolygon[(i + 1) % shaftPolygon.length];
      const overlap = getEdgeOverlapOnWall(edgeStart, edgeEnd, wallStart, wallEnd);
      totalOverlap += overlap;
    }

    if (totalOverlap === 0) return 0;

    // Wandhöhe an der Überlappungsstelle (Durchschnitt)
    const h1 = wallStart.z ?? wall.startHeight ?? storeyHeight;
    const h2 = wallEnd.z ?? wall.endHeight ?? storeyHeight;
    const avgHeight = (h1 + h2) / 2;

    // Abzugsfläche = Überlappungslänge × Wandhöhe
    return totalOverlap * avgHeight;
  }, [points, getShaftPolygon, storeyHeight]);

  // Berechne Vorwand-Abzugsfläche für eine Wand (in mm²)
  // Vorwände werden nur mit ihrer eigenen Höhe abgezogen
  const getStubWallDeductionForWall = useCallback((wall, stubWall) => {
    const wallStart = points.find(p => p.id === wall.startPointId);
    const wallEnd = points.find(p => p.id === wall.endPointId);
    if (!wallStart || !wallEnd) return 0;

    const stubWallPolygon = getStubWallPolygon(stubWall);
    if (stubWallPolygon.length < 2) return 0;

    // Prüfe jede Kante des Vorwand-Polygons
    let totalOverlap = 0;
    for (let i = 0; i < stubWallPolygon.length; i++) {
      const edgeStart = stubWallPolygon[i];
      const edgeEnd = stubWallPolygon[(i + 1) % stubWallPolygon.length];
      const overlap = getEdgeOverlapOnWall(edgeStart, edgeEnd, wallStart, wallEnd);
      totalOverlap += overlap;
    }

    if (totalOverlap === 0) return 0;

    // Abzugsfläche = Überlappungslänge × Vorwand-Höhe
    return totalOverlap * (stubWall.height || 0);
  }, [points, getStubWallPolygon]);

  // Gesamte Schacht- und Vorwand-Abzugsfläche für eine Wand (in mm²)
  const getWallDeductionsArea = useCallback((wall) => {
    let totalDeduction = 0;

    // Schacht-Abzüge
    shafts.forEach(shaft => {
      totalDeduction += getShaftDeductionForWall(wall, shaft);
    });

    // Vorwand-Abzüge
    stubWalls.forEach(stubWall => {
      totalDeduction += getStubWallDeductionForWall(wall, stubWall);
    });

    return totalDeduction;
  }, [shafts, stubWalls, getShaftDeductionForWall, getStubWallDeductionForWall]);

  // Unbenutzte Punkte aufräumen
  const cleanupOrphanedPoints = useCallback(() => {
    const usedPointIds = new Set();

    walls.forEach(w => {
      usedPointIds.add(w.startPointId);
      usedPointIds.add(w.endPointId);
    });

    spaces.forEach(s => {
      (s.pointIds || []).forEach(pid => usedPointIds.add(pid));
    });

    // Schacht-Punkte auch behalten
    shafts.forEach(s => {
      (s.pointIds || []).forEach(pid => usedPointIds.add(pid));
    });

    // Vorwand-Punkte auch behalten
    stubWalls.forEach(sw => {
      (sw.pointIds || []).forEach(pid => usedPointIds.add(pid));
    });

    setPoints(prev => prev.filter(p => usedPointIds.has(p.id)));
  }, [walls, spaces, shafts, stubWalls]);

  // Bulk-Import (für OBJ-Import)
  const importData = useCallback(({ points: newPoints, walls: newWalls, arcs: newArcs }) => {
    setPoints(prev => [...prev, ...newPoints]);
    setWalls(prev => [...prev, ...newWalls]);
    if (newArcs) {
      setArcs(prev => [...prev, ...newArcs]);
    }
  }, []);

  // ============================================
  // SOLID-OPERATIONEN (3D-Körper)
  // ============================================

  // Solid hinzufügen
  const addSolid = useCallback((solid) => {
    setSolids(prev => [...prev, solid]);
    saveSnapshot();
    return solid.id;
  }, [saveSnapshot]);

  // Solid aktualisieren
  const updateSolid = useCallback((solidId, updates) => {
    setSolids(prev => prev.map(s =>
      s.id === solidId ? { ...s, ...updates } : s
    ));
    saveSnapshot();
  }, [saveSnapshot]);

  // Solid verschieben
  const moveSolid = useCallback((solidId, newOrigin) => {
    setSolids(prev => prev.map(s =>
      s.id === solidId ? { ...s, origin: newOrigin } : s
    ));
  }, []);

  // Solid mit Snapshot verschieben (für DragEnd)
  const moveSolidWithSnapshot = useCallback((solidId, newOrigin) => {
    moveSolid(solidId, newOrigin);
    saveSnapshot();
  }, [moveSolid, saveSnapshot]);

  // Solid löschen
  const deleteSolid = useCallback((solidId) => {
    setSolids(prev => prev.filter(s => s.id !== solidId));
    saveSnapshot();
  }, [saveSnapshot]);

  // Solid finden
  const getSolid = useCallback((solidId) => {
    return solids.find(s => s.id === solidId);
  }, [solids]);

  // Alles zurücksetzen
  const reset = useCallback(() => {
    setPoints([]);
    setWalls([]);
    setSpaces([]);
    setShafts([]);
    setStubWalls([]);
    setArcs([]);
    setSolids([]);
  }, []);

  // Wandlänge berechnen
  const getWallLength = useCallback((wall) => {
    const start = points.find(p => p.id === wall.startPointId);
    const end = points.find(p => p.id === wall.endPointId);
    if (!start || !end) return 0;
    return distance(start, end);
  }, [points]);

  // Öffnungsfläche einer Wand berechnen
  const getWallOpeningsArea = useCallback((wall) => {
    return (wall.openings || []).reduce((sum, o) => {
      return sum + (o.width || 0) * (o.height || 0);
    }, 0);
  }, []);

  // Netto-Wandfläche berechnen (Trapez-Formel für unterschiedliche Höhen)
  const getWallNetArea = useCallback((wall) => {
    const length = getWallLength(wall);
    // Trapez-Fläche: (h1 + h2) / 2 * Länge
    const h1 = wall.startHeight ?? storeyHeight;
    const h2 = wall.endHeight ?? storeyHeight;
    const grossArea = ((h1 + h2) / 2) * length;
    const openingsArea = getWallOpeningsArea(wall);
    return grossArea - openingsArea;
  }, [getWallLength, getWallOpeningsArea, storeyHeight]);

  // Durchschnittliche Deckenhöhe eines Raums berechnen
  const getSpaceAvgCeilingHeight = useCallback((space) => {
    const polygon = getSpacePolygon(space);
    if (polygon.length === 0) return storeyHeight;

    const totalHeight = polygon.reduce((sum, p) => sum + (p.z ?? storeyHeight), 0);
    return totalHeight / polygon.length;
  }, [getSpacePolygon, storeyHeight]);

  // Raumvolumen berechnen (Grundfläche × Durchschnittshöhe)
  const getSpaceVolume = useCallback((space) => {
    const polygon = getSpacePolygon(space);
    const floorAreaMm2 = calcPolygonArea(polygon);
    const avgHeightMm = getSpaceAvgCeilingHeight(space);
    // Volumen in m³: (mm² * mm) / 1e9
    return (floorAreaMm2 * avgHeightMm) / 1000000000;
  }, [getSpacePolygon, getSpaceAvgCeilingHeight]);

  // Daten für Export aufbereiten
  const exportData = useMemo(() => {
    const spacesWithAreas = spaces.map(space => {
      const polygon = getSpacePolygon(space);
      const floorArea = calcPolygonArea(polygon) / 1000000; // mm² zu m²
      const avgCeilingHeight = getSpaceAvgCeilingHeight(space) / 1000; // mm zu m
      const volume = getSpaceVolume(space); // m³
      return { ...space, floorArea, avgCeilingHeight, volume };
    });

    // Schächte mit Flächen
    const shaftsWithAreas = shafts.map(shaft => {
      const polygon = getShaftPolygon(shaft);
      const area = calcPolygonArea(polygon) / 1000000; // mm² zu m²
      return { ...shaft, area };
    });

    // Vorwände mit berechneten Flächen (Polygon-basiert)
    const stubWallsWithData = stubWalls.map(sw => {
      const polygon = getStubWallPolygon(sw);
      const groundArea = calcPolygonArea(polygon) / 1000000; // Grundfläche in m²
      const wallArea = getStubWallArea(sw); // Wandfläche (Umfang × Höhe) in m²
      return {
        ...sw,
        height: sw.height / 1000, // mm zu m
        groundArea, // Grundfläche der Vorwand
        wallArea, // Wandfläche der Vorwand
      };
    });

    // Wände mit berechneten Werten (Trapez-Formel für unterschiedliche Höhen)
    const wallsWithData = walls.map(wall => {
      const length = getWallLength(wall);
      const h1 = wall.startHeight ?? storeyHeight;
      const h2 = wall.endHeight ?? storeyHeight;
      // Trapez-Fläche: (h1 + h2) / 2 * Länge
      const grossArea = ((h1 + h2) / 2) * length;
      const openingsArea = getWallOpeningsArea(wall);
      const deductionsArea = getWallDeductionsArea(wall); // Schacht/Vorwand-Abzüge
      const netArea = (grossArea / 1000000) - (openingsArea / 1000000) - (deductionsArea / 1000000);

      return {
        ...wall,
        length: length / 1000, // mm zu m
        startHeight: h1 / 1000, // mm zu m
        endHeight: h2 / 1000,   // mm zu m
        avgHeight: ((h1 + h2) / 2) / 1000, // Durchschnittshöhe für Anzeige
        isTrapezoid: h1 !== h2, // Flag für Trapez-Wand
        grossArea: grossArea / 1000000, // mm² zu m²
        openingsArea: openingsArea / 1000000,
        deductionsArea: deductionsArea / 1000000, // Schacht/Vorwand-Abzüge in m²
        netArea: Math.max(0, netArea),
      };
    });

    return {
      points,
      walls: wallsWithData,
      spaces: spacesWithAreas,
      shafts: shaftsWithAreas,
      stubWalls: stubWallsWithData,
      arcs, // Arc-Metadaten für Speicherung
      solids, // 3D-Körper
    };
  }, [points, walls, spaces, shafts, stubWalls, arcs, solids, getSpacePolygon, getShaftPolygon, getStubWallPolygon, getWallLength, getWallOpeningsArea, getWallDeductionsArea, getStubWallArea, storeyHeight]);

  // Mengen-Zusammenfassung
  const quantities = useMemo(() => {
    const wallsByType = {};
    let totalFloorArea = 0; // Gesamte Grundfläche (= Deckenfläche auf Geschoss-Ebene)
    let totalVolume = 0; // Gesamtes Raumvolumen

    // Wände nach Typ gruppieren
    walls.forEach(wall => {
      const typeRef = wall.typeRef || 'unassigned';
      if (!wallsByType[typeRef]) {
        wallsByType[typeRef] = {
          count: 0,
          totalLength: 0,
          grossArea: 0,
          openingsArea: 0,
          deductionsArea: 0, // Schacht/Vorwand-Abzüge
          netArea: 0,
        };
      }

      const length = getWallLength(wall);
      // Höhen aus Punkten holen (nicht aus storeyHeight!)
      const startPoint = points.find(p => p.id === wall.startPointId);
      const endPoint = points.find(p => p.id === wall.endPointId);
      const h1 = startPoint?.z ?? wall.startHeight ?? storeyHeight;
      const h2 = endPoint?.z ?? wall.endHeight ?? storeyHeight;
      // Trapez-Fläche: (h1 + h2) / 2 * Länge
      const grossArea = ((h1 + h2) / 2) * length;
      const openingsArea = getWallOpeningsArea(wall);
      const deductionsArea = getWallDeductionsArea(wall); // Schacht/Vorwand-Abzüge

      wallsByType[typeRef].count++;
      wallsByType[typeRef].totalLength += length;
      wallsByType[typeRef].grossArea += grossArea;
      wallsByType[typeRef].openingsArea += openingsArea;
      wallsByType[typeRef].deductionsArea += deductionsArea;
      wallsByType[typeRef].netArea += (grossArea - openingsArea - deductionsArea);
    });

    // Gesamte Grundfläche und Volumen berechnen
    spaces.forEach(space => {
      const polygon = getSpacePolygon(space);
      const area = calcPolygonArea(polygon);
      totalFloorArea += area;

      // Volumen berechnen (Fläche × Durchschnittshöhe)
      const avgHeight = getSpaceAvgCeilingHeight(space);
      totalVolume += (area * avgHeight) / 1000000000; // mm² * mm -> m³
    });

    // In m/m² umrechnen
    Object.values(wallsByType).forEach(data => {
      data.totalLength /= 1000;
      data.grossArea /= 1000000;
      data.openingsArea /= 1000000;
      data.deductionsArea /= 1000000;
      data.netArea /= 1000000;
    });

    totalFloorArea /= 1000000; // mm² zu m²

    return { wallsByType, totalFloorArea, totalVolume };
  }, [points, walls, spaces, getWallLength, getWallOpeningsArea, getWallDeductionsArea, getSpacePolygon, getSpaceAvgCeilingHeight, storeyHeight]);

  return {
    // State
    points,
    walls,
    spaces,
    shafts,
    stubWalls,
    storeyHeight,

    // Punkt-Operationen
    getPoint,
    findNearestPoint,
    findWallAtPoint,
    addPoint,
    movePoint,
    updatePoint,
    deletePoint,
    mergePoints,

    // Wand-Operationen
    findWallBetweenPoints,
    addWall,
    addDividingWall,
    updateWall,
    deleteWall,
    mergeWalls, // Issue #11: Zwei Wände zusammenführen
    divideWall, // Issue #11: Wand in Segmente teilen
    splitWallAtPoint,
    getWallLength,
    getWallOpeningsArea,
    getWallNetArea,

    // Arc-Operationen (Bögen)
    arcs,
    createArc,
    updateArc,
    updateArcStart,
    updateArcEnd,
    updateArcSegments,
    deleteArc,
    explodeArc,
    getArcForThroughPoint,
    getArcForStartPoint,
    getArcForEndPoint,
    getArcForPoint,

    // Öffnungen
    addOpeningToWall,
    updateOpening,
    deleteOpening,

    // Raum-Operationen
    getSpacePolygon,
    getWallsForSpace,
    addSpace,
    updateSpace,
    deleteSpace,
    splitSpaceWithWall,
    getSpaceAvgCeilingHeight,
    getSpaceVolume,

    // Schacht-Operationen
    addShaft,
    updateShaft,
    deleteShaft,
    getShaftPolygon,

    // Vorwand-Operationen
    addStubWall,
    updateStubWall,
    deleteStubWall,
    getStubWallPolygon,
    getStubWallArea,

    // Solid-Operationen (3D-Körper)
    solids,
    addSolid,
    updateSolid,
    moveSolid,
    moveSolidWithSnapshot,
    deleteSolid,
    getSolid,

    // Utilities
    cleanupOrphanedPoints,
    importData,
    reset,
    exportData,
    quantities,

    // History / Undo-Redo
    undo,
    redo,
    canUndo,
    canRedo,
    saveSnapshot, // Manuell aufrufen z.B. bei DragEnd
    movePointWithSnapshot, // Für DragEnd
  };
}
